import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ReplyReach,
  useListMarketDialogueQuestionsQuery,
  useReplyToMarketDialogueQuestionMutation,
  useShareWithMarketDialogueParticipantsMutation,
  type AskedQuestionDto,
  type MarketDialogueDto,
} from "../../autogen/bff-api";
import { AnonymizationBanner } from "../../common/components/communication/AnonymizationBanner";
import { useApiError } from "../../common/errors/useApiError";
import { TextLengthHint } from "../../common/input/TextLengthHint";
import { useDisplayDateWithTime } from "../../common/formatting/useDisplayDateWithTime";
import { MarketDialogueFormSection } from "./MarketDialogueFormSection";
import { SharedWithParticipants } from "./SharedWithParticipants";
import { MAX_SUPPLIER_QUESTION_LENGTH } from "./supplier/SupplierQuestions";

const MAX_SHARED_TEXT_LENGTH = 10000;
const MAX_NOTICE_TITLE_LENGTH = 2000;

export const MarketDialogueConversation = ({ dialogue }: { dialogue: MarketDialogueDto }) => {
  const { t } = useTranslation();
  const { show } = useApiError();
  const { data, error, isLoading } = useListMarketDialogueQuestionsQuery({ marketDialogueId: dialogue.id });

  return (
    <Stack spacing="6">
      <MarketDialogueFormSection heading={t("Questions from suppliers")}>
        {error ? (
          show(error)
        ) : isLoading || !data ? (
          <Flex justify="center" py="8">
            <Spinner color="smPrimary" />
          </Flex>
        ) : data.questions.length === 0 ? (
          <Text color="smMuted" fontSize="sm">
            {t("No supplier has asked a question yet.")}
          </Text>
        ) : (
          <Stack spacing="3">
            {data.questions.map((asked) => (
              <AskedQuestion
                key={asked.question.id}
                marketDialogueId={dialogue.id}
                asked={asked}
                canReply={data.canReply}
              />
            ))}
          </Stack>
        )}
      </MarketDialogueFormSection>
      <MarketDialogueFormSection heading={t("Shared with every participant")}>
        <Stack spacing="4">
          {dialogue.canShare && <ShareNotice marketDialogueId={dialogue.id} />}
          <SharedWithParticipants
            shared={dialogue.shared ?? []}
            documents={dialogue.documents}
            emptyText={t("Nothing has been shared with every participant yet.") ?? undefined}
          />
          {dialogue.canShare && (
            <Text fontSize="xs" color="smMuted">
              {t("A document you add under «Documents» while the dialogue is open is shared with every participant.")}
            </Text>
          )}
        </Stack>
      </MarketDialogueFormSection>
    </Stack>
  );
};

const AskedQuestion = ({
  marketDialogueId,
  asked,
  canReply,
}: {
  marketDialogueId: string;
  asked: AskedQuestionDto;
  canReply: boolean;
}) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const displayDateWithTime = useDisplayDateWithTime();
  const [text, setText] = useState("");
  const [reach, setReach] = useState<ReplyReach>(ReplyReach.Asker);
  const [sharedQuestion, setSharedQuestion] = useState(asked.question.text);
  const [reply, { isLoading }] = useReplyToMarketDialogueQuestionMutation();
  const { question } = asked;

  const send = async () => {
    try {
      await reply({
        marketDialogueId,
        answerId: asked.answerId,
        questionId: question.id,
        replyToSupplierQuestionRequest: {
          text,
          reach,
          sharedQuestion: reach === ReplyReach.AllParticipants ? sharedQuestion : undefined,
        },
      }).unwrap();
      setText("");
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="md" p="3" data-testid="asked-question">
      <HStack spacing="2" wrap="wrap">
        <Text fontWeight="semibold" fontSize="sm">
          {asked.supplierName}
        </Text>
        <Text fontSize="xs" color="smMuted">
          {displayDateWithTime(question.askedAt)}
        </Text>
      </HStack>
      <Text whiteSpace="pre-wrap" mt="1">
        {question.text}
      </Text>
      {question.reply ? (
        <Box mt="2" pl="3" borderLeftWidth="3px" borderColor="smBorder">
          <Text fontSize="xs" color="smMuted">
            {question.reply.reach === ReplyReach.AllParticipants
              ? t("Answered every participant, without the supplier's name")
              : t("Answered {{name}} only", { name: asked.supplierName })}
          </Text>
          <Text whiteSpace="pre-wrap" fontSize="sm">
            {question.reply.text}
          </Text>
        </Box>
      ) : canReply ? (
        <FormControl mt="3">
          <FormLabel fontSize="sm">{t("Your answer")}</FormLabel>
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={MAX_SHARED_TEXT_LENGTH} />
          <TextLengthHint text={text} max={MAX_SHARED_TEXT_LENGTH} />
          <RadioGroup value={reach} onChange={(value) => setReach(value as ReplyReach)} mt="2">
            <Stack spacing="1">
              <Radio value={ReplyReach.Asker} size="sm">
                {t("Only to {{name}}", { name: asked.supplierName })}
              </Radio>
              <Radio value={ReplyReach.AllParticipants} size="sm">
                {t("To every participant, without the name of the supplier who asked")}
              </Radio>
            </Stack>
          </RadioGroup>
          {reach === ReplyReach.AllParticipants && (
            <Stack spacing="2" mt="3">
              <AnonymizationBanner sharedWith="dialogueParticipants" />
              <FormControl>
                <FormLabel fontSize="sm">{t("The question as every participant will read it")}</FormLabel>
                <Textarea
                  value={sharedQuestion}
                  onChange={(e) => setSharedQuestion(e.target.value)}
                  rows={2}
                  maxLength={MAX_SUPPLIER_QUESTION_LENGTH}
                />
                <TextLengthHint text={sharedQuestion} max={MAX_SUPPLIER_QUESTION_LENGTH} />
              </FormControl>
            </Stack>
          )}
          <Button
            mt="2"
            size="sm"
            colorScheme="teal"
            onClick={() => void send()}
            isLoading={isLoading}
            isDisabled={
              text.trim().length === 0 || (reach === ReplyReach.AllParticipants && sharedQuestion.trim().length === 0)
            }
          >
            {t("Send answer")}
          </Button>
        </FormControl>
      ) : (
        <Text mt="2" fontSize="sm" color="smMuted">
          {t("Not answered yet")}
        </Text>
      )}
    </Box>
  );
};

const ShareNotice = ({ marketDialogueId }: { marketDialogueId: string }) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [share, { isLoading }] = useShareWithMarketDialogueParticipantsMutation();

  const send = async () => {
    try {
      await share({ marketDialogueId, shareWithParticipantsRequest: { title, text } }).unwrap();
      setTitle("");
      setText("");
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <Stack spacing="2" data-testid="share-notice">
      <FormControl>
        <FormLabel fontSize="sm">{t("Title")}</FormLabel>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={MAX_NOTICE_TITLE_LENGTH} />
        <TextLengthHint text={title} max={MAX_NOTICE_TITLE_LENGTH} />
      </FormControl>
      <FormControl>
        <FormLabel fontSize="sm">{t("What you share, for example a change to the draft")}</FormLabel>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={MAX_SHARED_TEXT_LENGTH} />
        <TextLengthHint text={text} max={MAX_SHARED_TEXT_LENGTH} />
      </FormControl>
      <Button
        alignSelf="flex-start"
        size="sm"
        colorScheme="teal"
        onClick={() => void send()}
        isLoading={isLoading}
        isDisabled={title.trim().length === 0 || text.trim().length === 0}
      >
        {t("Share with every participant")}
      </Button>
    </Stack>
  );
};
